import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, fmtDate, fmtTime, minutesToHm } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { useGo } from '../../../navigation/nav';
import { Badge, Button, CalChip, CalendarMonth, Card, DescList, Grid, Loading, Modal, PageHeader, Row, Screen, StatCard, StatusBadge, Text, useTheme } from '../../../ui';
import { attBg, attColor, AttendanceLegend } from '../components/attendance';

export function MyAttendanceScreen() {
  const [month, setMonth] = useState(currentPeriod());
  const [sel, setSel] = useState<string | null>(null);
  const go = useGo();
  const { colors } = useTheme();
  const q = useQuery({ queryKey: ['attendance', 'my', month], queryFn: () => api.attendance.my(month) });
  const hol = useQuery({ queryKey: ['holidays', 'my', month.slice(0, 4)], queryFn: () => api.leave.myHolidays(Number(month.slice(0, 4))) });
  const days = q.data ?? [];
  const byDate = new Map(days.map((d) => [d.date, d]));
  const holidays = new Map((hol.data ?? []).map((h) => [h.date, h]));
  const count = (s: string) => days.filter((d) => d.status === s).length;
  const worked = days.reduce((a, d) => a + d.worked_minutes, 0);
  const d = sel ? byDate.get(sel) : undefined;
  const regularise = (date: string) => {
    setSel(null);
    go('Regularise', { date });
  };
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()}>
      <PageHeader
        title="My attendance"
        actions={
          <>
            <Button icon="clock" variant="primary" title="Punch in / out" onPress={() => go('Punch')} />
            <Button icon="edit" title="Regularise" onPress={() => go('Regularise')} />
          </>
        }
      />
      <Grid min={150}>
        <StatCard label="Present" value={count('present') + count('on_duty')} icon="check" tone="success" />
        <StatCard label="Absent" value={count('absent')} icon="x" tone="danger" />
        <StatCard label="Leave" value={count('leave') + count('half_leave') / 2} icon="leaf" tone="info" />
        <StatCard label="Late marks" value={days.filter((x) => x.is_late).length} icon="clock" tone="warning" />
        <StatCard label="Hours worked" value={minutesToHm(worked)} icon="chart" tone="primary" />
      </Grid>
      <Card>
        {q.isLoading ? (
          <Loading />
        ) : (
          <CalendarMonth
            month={month}
            onMonthChange={setMonth}
            onDayPress={setSel}
            dayBackground={(date) => {
              const a = byDate.get(date);
              return a ? attBg(colors, a.status) : holidays.has(date) ? colors.infoSoft : undefined;
            }}
            renderDay={(date) => {
              const a = byDate.get(date);
              const h = holidays.get(date);
              return (
                <>
                  {a && <CalChip color={attColor(colors, a.status)}>{humanize(a.status)}</CalChip>}
                  {a && a.worked_minutes > 0 && (
                    <Text size={10} variant="small" numberOfLines={1}>
                      {minutesToHm(a.worked_minutes)}
                      {a.is_late ? ' · late' : ''}
                    </Text>
                  )}
                  {!a && h && <CalChip color={colors.info}>{h.name}</CalChip>}
                </>
              );
            }}
          />
        )}
        <View style={{ marginTop: 12 }}>
          <AttendanceLegend colors={colors} />
        </View>
      </Card>
      <Modal open={!!sel} onClose={() => setSel(null)} title={sel ? fmtDate(sel) : ''} width={440}>
        {d ? (
          <>
            <Row>
              <StatusBadge status={d.status} />
              {d.is_late && <Badge tone="warning">Late {d.late_minutes}m</Badge>}
              {d.is_locked && <Badge tone="neutral">Locked</Badge>}
            </Row>
            <DescList
              items={[
                ['First in', fmtTime(d.first_in)],
                ['Last out', fmtTime(d.last_out)],
                ['Worked', minutesToHm(d.worked_minutes)],
                ['Overtime', minutesToHm(d.ot_minutes)],
                ['Early exit', `${d.early_exit_minutes}m`],
                ['LOP days', d.lop_days],
                ['Remarks', d.remarks],
              ]}
            />
            {!d.is_locked && <Button variant="primary" icon="edit" title="Regularise this day" onPress={() => regularise(d.date)} />}
          </>
        ) : (
          <>
            <Text>{(sel && holidays.get(sel)?.name) ?? 'No attendance record for this day.'}</Text>
            {sel && <Button variant="primary" icon="edit" title="Regularise this day" onPress={() => regularise(sel)} />}
          </>
        )}
      </Modal>
    </Screen>
  );
}
